
import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { JournalFormValues } from "@/components/journal/form/JournalFormSchema";
import HelpTooltip from "@/components/help/HelpTooltip";

interface PromptResponseFieldProps {
  form: UseFormReturn<JournalFormValues>;
  name: keyof JournalFormValues;
  label: string;
  onUserStartedTyping?: () => void;
}

const PromptResponseField: React.FC<PromptResponseFieldProps> = ({ 
  form, 
  name, 
  label,
  onUserStartedTyping 
}) => {
  const handleChange = (value: string) => {
    // Call the field onChange to update form state
    form.setValue(name, value, { shouldValidate: true });
    
    // Notify parent that user started typing
    if (onUserStartedTyping && value.length > 0) {
      onUserStartedTyping();
    }
  };

  const fieldId = `${name}-field`;
  const descriptionId = `${name}-description`;
  const errorId = `${name}-error`;

  const getHelpContent = () => {
    switch (name) {
      case 'perception_response':
        return "Focus on what you're noticing right now - physical sensations, emotions, thoughts, or your environment. There's no wrong answer.";
      case 'intention_response':
        return "Connect your experience to what you want to achieve or how you want to feel. What's your deeper purpose here?";
      case 'next_step_response':
        return "Think of one small, concrete action you can take. It doesn't have to solve everything - just move you forward.";
      default:
        return "Take your time to reflect honestly. Your authentic response is what matters most.";
    }
  };

  const getTutorialAttribute = () => {
    switch (name) {
      case 'perception_response':
        return "perception-field";
      case 'intention_response':
        return "intention-field";
      case 'next_step_response':
        return "next-step-field";
      default:
        return undefined;
    }
  };

  return (
    <div data-tutorial={getTutorialAttribute()}>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormLabel id={`${fieldId}-label`} className="flex items-center gap-2">
              {label}
              <HelpTooltip 
                content={getHelpContent()}
                side="right"
              />
            </FormLabel>
            <FormDescription id={descriptionId}>
              Take your time to reflect on this prompt. There are no right or wrong answers.
            </FormDescription>
            <FormControl>
              <Textarea
                placeholder="Enter your response here..."
                className="min-h-32"
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e.target.value);
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                aria-labelledby={`${fieldId}-label`}
                aria-describedby={`${descriptionId} ${errorId}`}
                aria-invalid={!!form.formState.errors[name]}
              />
            </FormControl>
            <FormMessage id={errorId} />
          </FormItem>
        )}
      />
    </div>
  );
};

export default PromptResponseField;
